import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ArrowDownTrayIcon, ShareIcon } from '@heroicons/react/24/outline';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { useAppSelector } from '../../hooks/redux';
import Button from './Button';
import Avatar from './Avatar';
import Logo from './Logo';
import toast from 'react-hot-toast';

interface SocialShareModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MESSAGE_OPTIONS = [
  {
    id: 'professional',
    label: 'Professional',
    text: "I've officially joined ClickCollab! 📸\nVisit my profile to explore my portfolio, check my services, and send me a collaboration request directly.\nLet's create something amazing together."
  },
  {
    id: 'short',
    label: 'Short & Punchy',
    text: "I'm now on ClickCollab. 🚀\nThe new home for my portfolio and bookings.\nSearch for me to view my work and let's collaborate!"
  },
  {
    id: 'community',
    label: 'Community',
    text: "Excited to be part of the ClickCollab community!\nA platform built for creators like us. Check out my latest work and raise a collaboration request directly on my profile."
  }
];

const SocialShareModal: React.FC<SocialShareModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAppSelector((state) => state.auth);
  // Default to Option C (Community) as per user feedback
  const [selectedOption, setSelectedOption] = useState(MESSAGE_OPTIONS[2]);
  const [customText, setCustomText] = useState(MESSAGE_OPTIONS[2].text);
  const [format, setFormat] = useState<'post' | 'story'>('post');
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [downloading, setDownloading] = useState(false);
  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  if (!isOpen || !user) return null;

  const profileUrl = `${window.location.origin}/profile/${user.id}`;

  const handleOptionSelect = (option: typeof MESSAGE_OPTIONS[0]) => {
    setSelectedOption(option);
    setCustomText(option.text);
  };

  const handleDownload = async () => {
    if (!cardRef.current) return;

    try {
      setDownloading(true);
      
      // Wait for images to load if any
      await new Promise(resolve => setTimeout(resolve, 100));

      // Create a clone of the card to render off-screen
      // This avoids issues with scaling, transforms, and scroll positions in the UI
      const originalCard = cardRef.current;
      const clone = originalCard.cloneNode(true) as HTMLElement;
      
      // Set fixed dimensions for the clone based on format
      // Post: 1080x1350 (4:5), Story: 1080x1920 (9:16)
      // We use a multiplier to ensure high quality
      const scale = 2;
      const width = format === 'post' ? 400 : 360;
      const height = format === 'post' ? 500 : 640;
      
      // Style the clone to be off-screen but visible to html2canvas
      clone.style.position = 'fixed';
      clone.style.top = '-10000px';
      clone.style.left = '-10000px';
      clone.style.width = `${width}px`;
      clone.style.height = `${height}px`;
      clone.style.transform = 'none';
      clone.style.borderRadius = '0'; // Remove border radius for the export if desired, or keep it
      clone.style.boxShadow = 'none';
      clone.style.zIndex = '-1';
      
      // Append to body
      document.body.appendChild(clone);

      const canvas = await html2canvas(clone, {
        scale: 3, // High resolution
        useCORS: true,
        backgroundColor: null,
        logging: false,
        width: width,
        height: height,
        windowWidth: width,
        windowHeight: height,
        onclone: (clonedDoc) => {
            // Fix for some specific elements if needed
            const clonedElement = clonedDoc.body.getElementsByTagName('div')[0];
            if (clonedElement) {
                // Ensure background shapes render correctly
                // Sometimes blur filters cause issues, we might want to adjust them here if needed
            }
        }
      });

      // Remove clone
      document.body.removeChild(clone);

      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `clickcollab-share-${user.first_name || 'profile'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Image downloaded successfully!');
    } catch (error) {
      console.error('Failed to generate image:', error);
      toast.error('Failed to generate image. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999999] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="relative bg-white dark:bg-gray-900 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto md:overflow-hidden shadow-2xl flex flex-col md:flex-row"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-50 p-2 bg-black/20 hover:bg-black/30 md:bg-white/10 md:hover:bg-white/20 rounded-full text-white md:text-gray-500 md:hover:bg-gray-100 md:dark:text-gray-400 md:dark:hover:bg-gray-800 transition-colors backdrop-blur-sm"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>

            {/* Mobile Scroll Container - Wraps Preview and Top Part of Controls */}
            <div className="flex-1 overflow-y-auto md:overflow-visible md:flex md:flex-row w-full overscroll-y-contain">
                {/* Left Side: Preview (The Card) */}
                <div className="w-full md:w-1/2 bg-gray-100 dark:bg-gray-800 p-6 flex items-center justify-center md:overflow-y-auto shrink-0">
                  <div 
                    ref={cardRef}
                    className={`${theme === 'dark' ? 'bg-black text-white' : 'bg-white text-black'} relative overflow-hidden shadow-2xl shrink-0 transition-all duration-300 flex flex-col ${
                      format === 'post' ? 'w-[400px] h-[500px]' : 'w-[360px] h-[640px]'
                    }`}
                  >
                    {/* Minimalist Background Shapes */}
                    <div className="absolute inset-0 pointer-events-none overflow-hidden">
                        {/* Top White Shapes - Simplified for Canvas */}
                        <div className={`absolute -top-[10%] -left-[10%] w-[40%] aspect-square rounded-full opacity-20 blur-2xl ${theme === 'dark' ? 'bg-white' : 'bg-black'}`}></div>
                        <div className={`absolute top-[5%] left-[20%] w-[2px] h-[100px] transform rotate-45 opacity-30 ${theme === 'dark' ? 'bg-white' : 'bg-black'}`}></div>
                        <div className={`absolute top-[10%] right-[30%] w-[60px] h-[60px] border rounded-full opacity-20 ${theme === 'dark' ? 'border-white' : 'border-black'}`}></div>
                        
                        {/* Large Circle Top Right */}
                        <div className={`absolute -top-[20%] -right-[20%] w-[70%] aspect-square rounded-full border opacity-10 ${theme === 'dark' ? 'border-white' : 'border-black'}`}></div>
                        
                        {/* Diagonal Line */}
                        <div className={`absolute top-1/2 left-0 w-full h-[1px] transform -rotate-12 opacity-20 ${theme === 'dark' ? 'bg-white' : 'bg-black'}`}></div>
                        
                        {/* Bottom Left Shape */}
                        <div className={`absolute -bottom-[10%] -left-[10%] w-[50%] aspect-square border rotate-45 opacity-10 ${theme === 'dark' ? 'border-white' : 'border-black'}`}></div>
                    </div>

                    {/* Content Container */}
                    <div className="relative z-10 w-full h-full flex flex-col p-8">
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-0 rounded-lg transition-all duration-300">
                              <Logo className="w-10 h-10 rounded-lg" />
                            </div>
                            <span className="font-bold text-xl tracking-tight">ClickCollab</span>
                        </div>

                        {/* Main Content Area */}
                        <div className="flex-1 flex flex-col items-center justify-center text-center w-full">
                            {/* Profile */}
                            <div className="mb-6 relative mx-auto">
                                <div className={`absolute inset-0 blur-xl rounded-full scale-110 ${theme === 'dark' ? 'bg-white/10' : 'bg-black/5'}`}></div>
                                <Avatar 
                                    src={user.profile_pic_thumb_signed || user.profile_pic} 
                                    alt={user.first_name} 
                                    size="xl" 
                                    className={`w-24 h-24 border-2 relative z-10 mx-auto ${theme === 'dark' ? 'border-white' : 'border-gray-200'}`}
                                />
                                <div className="mt-3 text-center">
                                    <h2 className="text-xl font-bold mb-1 tracking-tight">
                                        {user.first_name} {user.last_name}
                                    </h2>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                                        {user.user_type || 'Creator'}
                                    </p>
                                </div>
                            </div>

                            {/* Message & QR Container */}
                            <div className={`w-full relative flex ${format === 'post' ? 'flex-row items-center gap-6 text-left' : 'flex-col items-center text-center'}`}>
                                {/* Message */}
                                <div className={`relative ${format === 'post' ? 'flex-1' : 'w-full px-2'}`}>
                                    <span className={`absolute text-teal-500/60 font-serif leading-none ${
                                        format === 'post' ? '-top-3 -left-3 text-5xl' : '-top-6 left-0 text-6xl'
                                    }`}>“</span>
                                    
                                    <p className={`relative z-10 font-serif italic leading-relaxed ${
                                        theme === 'dark' ? 'text-gray-100' : 'text-gray-800'
                                    } ${
                                        format === 'post' ? 'text-sm pl-2' : 'text-base px-4'
                                    }`}>
                                        {customText}
                                    </p>
                                    
                                    <span className={`absolute text-teal-500/60 font-serif leading-none ${
                                        format === 'post' ? '-bottom-6 right-0 text-5xl' : '-bottom-8 right-0 text-6xl'
                                    }`}>”</span>
                                </div>

                                {/* QR Code - Side for Post, Bottom for Story */}
                                {format === 'post' && (
                                    <div className="bg-white p-2 rounded-xl shrink-0 shadow-lg transform rotate-2">
                                        <QRCodeSVG 
                                            value={profileUrl} 
                                            size={64}
                                            level="M"
                                            fgColor="#000000"
                                            marginSize={0}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer - Only for Story (Post has QR on side) */}
                        {format === 'story' ? (
                            <div className={`mt-8 pt-6 border-t flex items-center justify-between ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
                                <div className="text-left">
                                    <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Scan to connect</p>
                                    <p className="text-sm font-bold tracking-widest">CLICKCOLLAB.IN</p>
                                </div>
                                <div className="bg-white p-2 rounded-lg">
                                    <QRCodeSVG 
                                        value={profileUrl} 
                                        size={56}
                                        level="M"
                                        fgColor="#000000"
                                        marginSize={0}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className={`mt-6 pt-4 border-t w-full text-center ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
                                 <p className="text-[10px] font-bold tracking-[0.3em] text-gray-500 uppercase">clickcollab.in</p>
                            </div>
                        )}
                    </div>
                  </div>
                </div>

                {/* Right Side: Controls */}
                <div className="w-full md:w-1/2 flex flex-col md:h-full">
                  <div className="p-6 md:p-8 flex-1 md:overflow-y-auto">
                      <div className="mb-6 flex-shrink-0">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                            <ShareIcon className="w-6 h-6 text-teal-600" />
                            Share Profile
                        </h2>
                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                            Generate a professional card to announce your presence on social media.
                        </p>
                      </div>

                      <div className="space-y-6">
                        {/* Format Selection */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Select Format
                                </label>
                                {/* Theme Toggle */}
                                <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                                    <button
                                        onClick={() => setTheme('dark')}
                                        className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                                            theme === 'dark'
                                                ? 'bg-white dark:bg-gray-700 text-black dark:text-white shadow-sm'
                                                : 'text-gray-500 hover:text-gray-900 dark:hover:text-gray-300'
                                        }`}
                                    >
                                        Dark
                                    </button>
                                    <button
                                        onClick={() => setTheme('light')}
                                        className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                                            theme === 'light'
                                                ? 'bg-white dark:bg-gray-700 text-black dark:text-white shadow-sm'
                                                : 'text-gray-500 hover:text-gray-900 dark:hover:text-gray-300'
                                        }`}
                                    >
                                        Light
                                    </button>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => setFormat('post')}
                                    className={`p-3 rounded-xl border text-sm font-medium transition-all flex flex-col items-center gap-2 ${
                                        format === 'post'
                                            ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 ring-1 ring-teal-500'
                                            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                                    }`}
                                >
                                    <div className="w-8 h-10 border-2 border-current rounded-sm opacity-50"></div>
                                    Post (4:5)
                                </button>
                                <button
                                    onClick={() => setFormat('story')}
                                    className={`p-3 rounded-xl border text-sm font-medium transition-all flex flex-col items-center gap-2 ${
                                        format === 'story'
                                            ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 ring-1 ring-teal-500'
                                            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                                    }`}
                                >
                                    <div className="w-6 h-10 border-2 border-current rounded-sm opacity-50"></div>
                                    Story (9:16)
                                </button>
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Select Message Style
                                </label>
                                <button
                                    onClick={() => setIsEditingMessage(true)}
                                    className="text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 text-xs font-medium flex items-center gap-1 transition-colors"
                                >
                                    <span className="text-lg">✎</span> Edit
                                </button>
                            </div>
                            <div className="space-y-3 mb-6">
                                {MESSAGE_OPTIONS.map((option) => (
                                    <button
                                        key={option.id}
                                        onClick={() => handleOptionSelect(option)}
                                        className={`w-full text-left p-4 rounded-xl border transition-all relative overflow-visible ${
                                            selectedOption.id === option.id
                                                ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 ring-1 ring-teal-500 z-10'
                                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <span className={`font-semibold text-sm ${
                                                selectedOption.id === option.id ? 'text-teal-700 dark:text-teal-400' : 'text-gray-900 dark:text-white'
                                            }`}>
                                                {option.label}
                                            </span>
                                            {selectedOption.id === option.id && (
                                                <div className="w-2 h-2 rounded-full bg-teal-500" />
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                                            {option.text}
                                        </p>
                                    </button>
                                ))}
                            </div>


                        </div>
                      </div>

                      </div>

                  {/* Desktop Footer - Fixed at Bottom */}
                  <div className="hidden md:block p-6 md:p-8 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 z-20">
                    <Button
                        onClick={handleDownload}
                        loading={downloading}
                        className="w-full justify-center py-3 text-base bg-teal-600 hover:bg-teal-700 text-white shadow-lg shadow-teal-500/20"
                    >
                        <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                        Download Image
                    </Button>
                    <p className="text-center text-xs text-gray-400 mt-3">
                        Perfect for Instagram, LinkedIn, and Twitter
                    </p>
                  </div>
                </div>
            </div>

            {/* Mobile Footer (Fixed) */}
            <div className="md:hidden p-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 z-20 shrink-0">
                <Button
                    onClick={handleDownload}
                    loading={downloading}
                    className="w-full justify-center py-3 text-base bg-teal-600 hover:bg-teal-700 text-white shadow-lg shadow-teal-500/20"
                >
                    <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                    Download Image
                </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>

    {/* Message Editing Modal */}
    <AnimatePresence>
        {isEditingMessage && (
            <div className="fixed inset-0 z-[10000000] flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setIsEditingMessage(false)}
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                />
                <motion.div
                    initial={{ scale: 0.95, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 20 }}
                    className="relative bg-white dark:bg-gray-900 rounded-2xl w-full max-w-lg p-6 shadow-2xl"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Edit Message</h3>
                        <button
                            onClick={() => setIsEditingMessage(false)}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                        >
                            <XMarkIcon className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                    
                    <div className="mb-6">
                        <textarea
                            value={customText}
                            onChange={(e) => setCustomText(e.target.value)}
                            rows={6}
                            className="w-full rounded-xl border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 focus:ring-teal-500 focus:border-teal-500 text-base p-4"
                            placeholder="Write your own message..."
                            autoFocus
                        />
                        <p className="text-xs text-gray-400 mt-2 text-right">
                            {customText.length} characters
                        </p>
                    </div>

                    <div className="flex justify-end gap-3">
                        <Button
                            variant="secondary"
                            onClick={() => setIsEditingMessage(false)}
                            className="w-full sm:w-auto"
                        >
                            Done
                        </Button>
                    </div>
                </motion.div>
            </div>
        )}
    </AnimatePresence>
    </>
  );
};

export default SocialShareModal;
